import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './ThemeManager.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const FONT_OPTIONS = [
    'Cormorant Garamond',
    'Inter',
    'Playfair Display',
    'Roboto',
    'Lora',
    'Montserrat',
    'Open Sans',
    'Raleway',
    'Poppins',
    'Merriweather',
    'Oswald',
    'Source Sans Pro',
    'PT Serif',
    'Nunito',
    'Libre Baskerville'
];

const CARD_STYLE_OPTIONS = [
    { value: 'minimal', label: 'Minimal — Clean edges, no borders' },
    { value: 'rounded', label: 'Rounded — Soft rounded corners' },
    { value: 'shadow', label: 'Shadow — Elevated with drop shadow' },
    { value: 'bordered', label: 'Bordered — Subtle border outline' }
];

const DEFAULT_THEME = {
    name: '',
    primaryColor: '#ffffff',
    secondaryColor: '#0a0a0a',
    accentColor: '#cc4455',
    backgroundColor: '#0a0a0a',
    textColor: '#e0e0e0',
    cardBackgroundColor: '#1a1a1a',
    headingFont: 'Cormorant Garamond',
    bodyFont: 'Inter',
    darkMode: true,
    cardStyle: 'minimal'
};

function ThemeManager() {
    const [themes, setThemes] = useState([]);
    const [activeThemeId, setActiveThemeId] = useState(null);
    const [editingTheme, setEditingTheme] = useState(null);
    const [formData, setFormData] = useState({ ...DEFAULT_THEME });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [showForm, setShowForm] = useState(false);

    useEffect(() => {
        fetchThemes();
    }, []);

    // Load Google Fonts dynamically for preview
    useEffect(() => {
        const fonts = [formData.headingFont, formData.bodyFont].filter(Boolean);
        fonts.forEach(font => {
            const fontId = `gfont-${font.replace(/\s+/g, '-').toLowerCase()}`;
            if (!document.getElementById(fontId)) {
                const link = document.createElement('link');
                link.id = fontId;
                link.rel = 'stylesheet';
                link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(font)}:wght@300;400;500;600;700&display=swap`;
                document.head.appendChild(link);
            }
        });
    }, [formData.headingFont, formData.bodyFont]);

    const fetchThemes = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_BASE}/themes`, { withCredentials: true });
            setThemes(response.data.themes);
            setActiveThemeId(response.data.activeThemeId);
            setError('');
        } catch (error) {
            console.error('Error fetching themes:', error);
            setError('Failed to load themes');
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = () => {
        setEditingTheme(null);
        setFormData({ ...DEFAULT_THEME });
        setShowForm(true);
        setMessage('');
        setError('');
    };

    const handleEdit = (theme) => {
        setEditingTheme(theme);
        setFormData({
            name: theme.name,
            primaryColor: theme.primary_color,
            secondaryColor: theme.secondary_color,
            accentColor: theme.accent_color,
            backgroundColor: theme.background_color,
            textColor: theme.text_color,
            cardBackgroundColor: theme.card_background_color || '#1a1a1a',
            headingFont: theme.heading_font,
            bodyFont: theme.body_font,
            darkMode: theme.dark_mode,
            cardStyle: theme.card_style
        });
        setShowForm(true);
        setMessage('');
        setError('');
    };

    const handleCancel = () => {
        setShowForm(false);
        setEditingTheme(null);
        setFormData({ ...DEFAULT_THEME });
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setMessage('');
        setError('');

        try {
            if (editingTheme) {
                await axios.put(`${API_BASE}/themes/${editingTheme.id}`, formData, { withCredentials: true });
                setMessage('Theme updated successfully!');
            } else {
                await axios.post(`${API_BASE}/themes`, formData, { withCredentials: true });
                setMessage('Theme created successfully!');
            }
            setShowForm(false);
            setEditingTheme(null);
            fetchThemes();
            setTimeout(() => setMessage(''), 3000);
        } catch (error) {
            console.error('Error saving theme:', error);
            setError(error.response?.data?.error || 'Failed to save theme');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (themeId) => {
        if (!window.confirm('Are you sure you want to delete this theme?')) return;

        try {
            await axios.delete(`${API_BASE}/themes/${themeId}`, { withCredentials: true });
            setMessage('Theme deleted successfully!');
            fetchThemes();
            setTimeout(() => setMessage(''), 3000);
        } catch (error) {
            console.error('Error deleting theme:', error);
            setError(error.response?.data?.error || 'Failed to delete theme');
        }
    };

    const handleActivate = async (themeId) => {
        try {
            await axios.put(`${API_BASE}/themes/${themeId}/activate`, {}, { withCredentials: true });
            setActiveThemeId(themeId);
            setMessage('Theme activated!');
            setTimeout(() => setMessage(''), 3000);
        } catch (error) {
            console.error('Error activating theme:', error);
            setError(error.response?.data?.error || 'Failed to activate theme');
        }
    };

    const handleDeactivate = async () => {
        try {
            await axios.delete(`${API_BASE}/themes/active`, { withCredentials: true });
            setActiveThemeId(null);
            setMessage('Theme deactivated — using default styling.');
            setTimeout(() => setMessage(''), 3000);
        } catch (error) {
            console.error('Error deactivating theme:', error);
            setError(error.response?.data?.error || 'Failed to deactivate theme');
        }
    };

    const handleDarkModePreset = () => {
        setFormData(prev => ({
            ...prev,
            darkMode: true,
            backgroundColor: '#0a0a0a',
            textColor: '#e0e0e0',
            primaryColor: '#ffffff',
            secondaryColor: '#0a0a0a',
            cardBackgroundColor: '#1a1a1a'
        }));
    };

    const handleLightModePreset = () => {
        setFormData(prev => ({
            ...prev,
            darkMode: false,
            backgroundColor: '#f5f5f5',
            textColor: '#222222',
            primaryColor: '#333333',
            secondaryColor: '#ffffff',
            cardBackgroundColor: '#ffffff'
        }));
    };

    if (loading) {
        return (
            <div className="theme-manager">
                <p>Loading themes...</p>
            </div>
        );
    }

    return (
        <div className="theme-manager">
            <div className="theme-manager-header">
                <div>
                    <h2>Themes</h2>
                    <p className="theme-description">
                        Customize the look and feel of your public gallery.
                    </p>
                </div>
                {!showForm && (
                    <button className="create-theme-btn" onClick={handleCreate}>
                        + New Theme
                    </button>
                )}
            </div>

            {error && <div className="error-message">{error}</div>}
            {message && <div className="success-message">{message}</div>}

            {showForm && (
                <form onSubmit={handleSave} className="theme-form">
                    <h3>{editingTheme ? 'Edit Theme' : 'Create New Theme'}</h3>

                    <div className="form-group">
                        <label htmlFor="themeName">Theme Name</label>
                        <input
                            type="text"
                            id="themeName"
                            value={formData.name}
                            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                            placeholder="e.g., Dark Elegance"
                            required
                            disabled={saving}
                        />
                    </div>

                    <div className="form-section">
                        <h4>Color Palette</h4>
                        <div className="color-grid">
                            <div className="color-field">
                                <label htmlFor="primaryColor">Primary</label>
                                <div className="color-input-wrapper">
                                    <input
                                        type="color"
                                        id="primaryColor"
                                        value={formData.primaryColor}
                                        onChange={(e) => setFormData(prev => ({ ...prev, primaryColor: e.target.value }))}
                                        disabled={saving}
                                    />
                                    <span className="color-value">{formData.primaryColor}</span>
                                </div>
                            </div>
                            <div className="color-field">
                                <label htmlFor="secondaryColor">Secondary</label>
                                <div className="color-input-wrapper">
                                    <input
                                        type="color"
                                        id="secondaryColor"
                                        value={formData.secondaryColor}
                                        onChange={(e) => setFormData(prev => ({ ...prev, secondaryColor: e.target.value }))}
                                        disabled={saving}
                                    />
                                    <span className="color-value">{formData.secondaryColor}</span>
                                </div>
                            </div>
                            <div className="color-field">
                                <label htmlFor="accentColor">Accent</label>
                                <div className="color-input-wrapper">
                                    <input
                                        type="color"
                                        id="accentColor"
                                        value={formData.accentColor}
                                        onChange={(e) => setFormData(prev => ({ ...prev, accentColor: e.target.value }))}
                                        disabled={saving}
                                    />
                                    <span className="color-value">{formData.accentColor}</span>
                                </div>
                            </div>
                            <div className="color-field">
                                <label htmlFor="backgroundColor">Background</label>
                                <div className="color-input-wrapper">
                                    <input
                                        type="color"
                                        id="backgroundColor"
                                        value={formData.backgroundColor}
                                        onChange={(e) => setFormData(prev => ({ ...prev, backgroundColor: e.target.value }))}
                                        disabled={saving}
                                    />
                                    <span className="color-value">{formData.backgroundColor}</span>
                                </div>
                            </div>
                            <div className="color-field">
                                <label htmlFor="textColor">Text</label>
                                <div className="color-input-wrapper">
                                    <input
                                        type="color"
                                        id="textColor"
                                        value={formData.textColor}
                                        onChange={(e) => setFormData(prev => ({ ...prev, textColor: e.target.value }))}
                                        disabled={saving}
                                    />
                                    <span className="color-value">{formData.textColor}</span>
                                </div>
                            </div>
                            <div className="color-field">
                                <label htmlFor="cardBackgroundColor">Card Background</label>
                                <div className="color-input-wrapper">
                                    <input
                                        type="color"
                                        id="cardBackgroundColor"
                                        value={formData.cardBackgroundColor}
                                        onChange={(e) => setFormData(prev => ({ ...prev, cardBackgroundColor: e.target.value }))}
                                        disabled={saving}
                                    />
                                    <span className="color-value">{formData.cardBackgroundColor}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="form-section">
                        <h4>Typography</h4>
                        <div className="font-grid">
                            <div className="form-group">
                                <label htmlFor="headingFont">Heading Font</label>
                                <select
                                    id="headingFont"
                                    value={formData.headingFont}
                                    onChange={(e) => setFormData(prev => ({ ...prev, headingFont: e.target.value }))}
                                    disabled={saving}
                                >
                                    {FONT_OPTIONS.map(font => (
                                        <option key={font} value={font}>{font}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label htmlFor="bodyFont">Body Font</label>
                                <select
                                    id="bodyFont"
                                    value={formData.bodyFont}
                                    onChange={(e) => setFormData(prev => ({ ...prev, bodyFont: e.target.value }))}
                                    disabled={saving}
                                >
                                    {FONT_OPTIONS.map(font => (
                                        <option key={font} value={font}>{font}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className="form-section">
                        <h4>Display Options</h4>
                        <div className="toggle-group">
                            <label className="toggle-label">
                                <span>Dark Mode</span>
                                <div className="toggle-switch-wrapper">
                                    <button
                                        type="button"
                                        className="preset-btn"
                                        onClick={handleDarkModePreset}
                                        disabled={saving}
                                    >
                                        Dark Preset
                                    </button>
                                    <button
                                        type="button"
                                        className="preset-btn"
                                        onClick={handleLightModePreset}
                                        disabled={saving}
                                    >
                                        Light Preset
                                    </button>
                                    <input
                                        type="checkbox"
                                        checked={formData.darkMode}
                                        onChange={(e) => setFormData(prev => ({ ...prev, darkMode: e.target.checked }))}
                                        disabled={saving}
                                        className="toggle-checkbox"
                                    />
                                    <span className="toggle-switch"></span>
                                </div>
                            </label>
                        </div>
                        <div className="form-group">
                            <label htmlFor="cardStyle">Card Style</label>
                            <select
                                id="cardStyle"
                                value={formData.cardStyle}
                                onChange={(e) => setFormData(prev => ({ ...prev, cardStyle: e.target.value }))}
                                disabled={saving}
                            >
                                {CARD_STYLE_OPTIONS.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="theme-preview-section">
                        <h4>Preview</h4>
                        <div
                            className="theme-preview"
                            style={{
                                background: formData.backgroundColor,
                                color: formData.textColor,
                                fontFamily: `'${formData.bodyFont}', sans-serif`
                            }}
                        >
                            <div className="preview-title" style={{
                                fontFamily: `'${formData.headingFont}', serif`,
                                color: formData.primaryColor
                            }}>
                                Gallery Title
                            </div>
                            <div className="preview-cards">
                                {[1, 2, 3].map(i => (
                                    <div
                                        key={i}
                                        className={`preview-card card-${formData.cardStyle}`}
                                        style={{
                                            background: formData.cardBackgroundColor,
                                            borderColor: formData.accentColor
                                        }}
                                    >
                                        <div className="preview-card-img" style={{ background: formData.secondaryColor }}></div>
                                        <div className="preview-card-text" style={{ color: formData.textColor }}>
                                            Photo {i}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="preview-accent" style={{ color: formData.accentColor }}>
                                Accent Color Sample
                            </div>
                        </div>
                    </div>

                    <div className="form-actions">
                        <button type="button" className="cancel-btn" onClick={handleCancel} disabled={saving}>
                            Cancel
                        </button>
                        <button type="submit" className="save-button" disabled={saving || !formData.name.trim()}>
                            {saving ? 'Saving...' : (editingTheme ? 'Update Theme' : 'Create Theme')}
                        </button>
                    </div>
                </form>
            )}

            {!showForm && (
                <div className="themes-list">
                    {themes.length === 0 ? (
                        <div className="no-themes">
                            <p>No themes yet. Create one to customize your public gallery.</p>
                        </div>
                    ) : (
                        themes.map(theme => (
                            <div key={theme.id} className={`theme-card ${activeThemeId === theme.id ? 'active' : ''}`}>
                                <div className="theme-card-preview" style={{ background: theme.background_color }}>
                                    <div className="theme-swatches">
                                        <span className="swatch" style={{ background: theme.primary_color }} title="Primary"></span>
                                        <span className="swatch" style={{ background: theme.secondary_color }} title="Secondary"></span>
                                        <span className="swatch" style={{ background: theme.accent_color }} title="Accent"></span>
                                        <span className="swatch" style={{ background: theme.text_color }} title="Text"></span>
                                    </div>
                                </div>
                                <div className="theme-card-info">
                                    <div className="theme-card-header">
                                        <h3>{theme.name}</h3>
                                        {activeThemeId === theme.id && (
                                            <span className="active-badge">Active</span>
                                        )}
                                    </div>
                                    <div className="theme-card-details">
                                        <span>{theme.heading_font} / {theme.body_font}</span>
                                        <span>{theme.dark_mode ? 'Dark' : 'Light'} Mode</span>
                                        <span style={{ textTransform: 'capitalize' }}>{theme.card_style} cards</span>
                                    </div>
                                    <div className="theme-card-actions">
                                        {activeThemeId === theme.id ? (
                                            <button className="deactivate-btn" onClick={handleDeactivate}>
                                                Deactivate
                                            </button>
                                        ) : (
                                            <button className="activate-btn" onClick={() => handleActivate(theme.id)}>
                                                Activate
                                            </button>
                                        )}
                                        <button className="edit-btn" onClick={() => handleEdit(theme)}>
                                            Edit
                                        </button>
                                        <button className="delete-btn" onClick={() => handleDelete(theme.id)}>
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
}

export default ThemeManager;
